import NiceModal, { useModal } from "@ebay/nice-modal-react"
import cx from "classnames"
import { Check, Image, LoaderCircle, RotateCcw, Search } from "lucide-react"
import { useCallback, useEffect, useState } from "react"
import {
    sessionActionAgentOptions,
    sessionActionTargetId,
    sessionActionTargetRange,
    type SessionActionAgentOption,
    type SessionActionTarget,
    type SessionActionTargets,
} from "../../actions/sessionActionTarget"
import type { SavedActionSessionLaunchKind } from "../../actions/actionTypes"
import { getNewTaskPinnedAgents } from "../../newTaskAgentOptions"
import { formatModelPricing, matchesModelSearch } from "../../modelCatalog"
import type { NewTaskAgentTarget } from "../../persistence/newTaskAgentPins"
import type { WildLarkTaskHarness } from "../../wildlark/client"
import { AgentProviderMark, agentProviderVisuals } from "../AgentProviderIdentity"
import { ThinkingPicker } from "../ThinkingPicker"
import type { ThinkingLevel } from "../../agentTarget"
import { Modal } from "../ui/Modal"

interface SessionActionPickerModalProps {
    actionName: string
    actionKind?: SavedActionSessionLaunchKind
    description?: string
    currentTarget?: SessionActionTarget
    pinnedTargets?: readonly NewTaskAgentTarget[]
    maxTargets?: number
    loadHarnesses: () => Promise<WildLarkTaskHarness[]>
    submitLabel?: string
    pendingLabel?: string
    instructionsLabel?: string
    initialTarget?: SessionActionTarget
    initialInstructions?: string
    onSelect: (targets: SessionActionTargets, instructions?: string) => void | Promise<void>
}

function AgentOptionButton({
    option,
    currentTarget,
    selected,
    selectionDisabled,
    onSelect,
}: {
    option: SessionActionAgentOption
    currentTarget?: SessionActionTarget
    selected: boolean
    selectionDisabled: boolean
    onSelect: (target: SessionActionTarget) => void
}) {
    const isCurrent = option.harnessId === currentTarget?.harnessId && option.modelId === currentTarget?.modelId && option.thinking === currentTarget?.thinking
    const visuals = agentProviderVisuals(option.harnessId)
    const pricing = formatModelPricing(option.pricing)
    return (
        <button
            type="button"
            className={cx(
                "btn flex min-h-14 w-full items-center justify-between gap-3 border px-4 py-2.5 text-left transition-colors",
                visuals.pickerButton,
                selected && "ring-2 ring-inset ring-primary",
                selectionDisabled && "cursor-not-allowed opacity-45"
            )}
            onClick={() => onSelect({ harnessId: option.harnessId, modelId: option.modelId, thinking: option.thinking })}
            disabled={selectionDisabled}
            aria-pressed={selected}
            data-provider={visuals.id}
        >
            <span className="flex min-w-0 items-center gap-3.5">
                <AgentProviderMark harnessId={option.harnessId} className={cx("h-5 w-5 shrink-0", visuals.pickerMark)} />
                <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold text-base-content">{option.modelLabel}</span>
                    <span className="block truncate text-xs text-muted">
                        {option.harnessLabel} · {option.thinking ?? "Max"}
                        {option.displayClass ? ` · ${option.displayClass}` : ""}
                        {option.nativeProvider ? ` · ${option.nativeProvider}` : ""}
                        {pricing ? ` · ${pricing}` : ""}
                    </span>
                </span>
            </span>
            <span className="flex shrink-0 flex-wrap justify-end gap-1">
                {selected && (
                    <span className="inline-flex items-center gap-1 bg-primary/15 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
                        <Check size={9} />
                        Selected
                    </span>
                )}
                {isCurrent && <span className="bg-base-300 px-1.5 py-0.5 text-[10px] font-semibold text-muted">Current</span>}
                {option.legacy && <span className="bg-base-300 px-1.5 py-0.5 text-[10px] font-semibold text-muted">Legacy</span>}
                {option.default && <span className="bg-base-300 px-1.5 py-0.5 text-[10px] font-semibold text-muted">Default</span>}
                {option.supportsImages && <Image size={13} className="text-muted" aria-label="Supports image input" />}
            </span>
        </button>
    )
}

function OptionSection({
    label,
    options,
    currentTarget,
    selectedOptionIds,
    selectionAtLimit,
    onSelect,
}: {
    label: string
    options: SessionActionAgentOption[]
    currentTarget?: SessionActionTarget
    selectedOptionIds: ReadonlySet<string>
    selectionAtLimit: boolean
    onSelect: (target: SessionActionTarget) => void
}) {
    if (options.length === 0) return null
    return (
        <section>
            <div className="mb-3 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted">
                <span>{label}</span>
                <span aria-hidden="true" className="h-px min-w-8 flex-1 bg-border" />
            </div>
            <div className="flex flex-col gap-2.5">
                {options.map((option) => (
                    <AgentOptionButton
                        key={option.id}
                        option={option}
                        currentTarget={currentTarget}
                        selected={selectedOptionIds.has(option.id)}
                        selectionDisabled={selectionAtLimit && !selectedOptionIds.has(option.id)}
                        onSelect={onSelect}
                    />
                ))}
            </div>
        </section>
    )
}

export const SessionActionPickerModal = NiceModal.create((props: SessionActionPickerModalProps) => {
    const {
        actionName,
        actionKind,
        description,
        currentTarget,
        pinnedTargets,
        maxTargets,
        loadHarnesses,
        onSelect,
        submitLabel,
        pendingLabel,
        instructionsLabel,
        initialTarget,
        initialInstructions,
    } = props
    const modal = useModal()
    const [harnesses, setHarnesses] = useState<WildLarkTaskHarness[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [thinking, setThinking] = useState<ThinkingLevel>(initialTarget?.thinking ?? currentTarget?.thinking ?? "max")
    const [selectedTargets, setSelectedTargets] = useState<SessionActionTarget[]>(() =>
        initialTarget ? [{ ...initialTarget, thinking: initialTarget.thinking ?? currentTarget?.thinking ?? "max" }] : []
    )
    const selectedOptionIds = new Set(selectedTargets.map(sessionActionTargetId))
    const [query, setQuery] = useState("")
    const [instructions, setInstructions] = useState(initialInstructions ?? "")
    const [submitting, setSubmitting] = useState(false)
    const [submitError, setSubmitError] = useState<string | null>(null)

    const load = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            setHarnesses(await loadHarnesses())
        } catch (loadError) {
            setError(loadError instanceof Error ? loadError.message : "Could not load available agents")
        } finally {
            setLoading(false)
        }
    }, [loadHarnesses])

    useEffect(() => {
        void load()
    }, [load])

    const options = sessionActionAgentOptions(harnesses).map((option) => ({ ...option, thinking, id: sessionActionTargetId({ ...option, thinking }) }))
    const pinned = getNewTaskPinnedAgents({
        options,
        pinned: pinnedTargets,
    })
    const pinnedIds = new Set(pinned.map((option) => option.id))
    const remaining = options.filter((option) => !pinnedIds.has(option.id))
    const matchesQuery = (option: SessionActionAgentOption) =>
        matchesModelSearch(query, [option.modelId, option.modelLabel, option.harnessId, option.harnessLabel, option.displayClass, option.nativeProvider])
    const visiblePinned = pinned.filter(matchesQuery)
    const other = remaining.filter((option) => !option.legacy && !option.default && matchesQuery(option))
    const legacy = remaining.filter((option) => option.legacy && matchesQuery(option))
    const defaults = remaining.filter((option) => option.default && matchesQuery(option))
    const visibleOptionCount = visiblePinned.length + other.length + legacy.length + defaults.length
    const selectedOptions = selectedTargets.flatMap((target) => {
        const option = options.find((candidate) => candidate.harnessId === target.harnessId && candidate.modelId === target.modelId)
        return option ? [{ ...option, ...target, id: sessionActionTargetId(target) }] : []
    })
    const targetRange = actionKind
        ? sessionActionTargetRange({ kind: actionKind, ...(maxTargets === undefined ? {} : { maxTargets }) })
        : { minTargets: 1, maxTargets: 1 }
    const allowsMultiple = targetRange.maxTargets > 1
    const selectionAtLimit = submitting || (allowsMultiple && selectedOptions.length >= targetRange.maxTargets)
    const unavailableHarnesses = harnesses.filter((harness) => !harness.installed || !harness.available)
    const currentHarness = currentTarget ? harnesses.find((harness) => harness.harnessId === currentTarget.harnessId) : undefined
    const currentModel = currentHarness?.models.find((model) => model.modelId === currentTarget?.modelId)
    const currentAgent = currentTarget
        ? {
              harnessLabel: currentHarness?.label ?? currentTarget.harnessId,
              modelLabel: currentModel?.label ?? currentTarget.modelId,
          }
        : null

    const select = (target: SessionActionTarget) => {
        if (submitting) return
        const targetId = sessionActionTargetId(target)
        if (!allowsMultiple && target.thinking) setThinking(target.thinking)
        setSelectedTargets((current) => {
            if (!allowsMultiple) return [target]
            if (current.some((entry) => sessionActionTargetId(entry) === targetId)) return current.filter((entry) => sessionActionTargetId(entry) !== targetId)
            return current.length < targetRange.maxTargets ? [...current, target] : current
        })
    }
    const changeThinking = (level: ThinkingLevel) => {
        setThinking(level)
        if (!allowsMultiple) setSelectedTargets((current) => current.map((target) => ({ ...target, thinking: level })))
    }

    const submit = async () => {
        const [first, ...rest] = selectedOptions
        if (!first || selectedOptions.length < targetRange.minTargets || submitting) return
        const targets: SessionActionTargets = [
            { harnessId: first.harnessId, modelId: first.modelId, thinking: first.thinking },
            ...rest.map((option) => ({ harnessId: option.harnessId, modelId: option.modelId, thinking: option.thinking })),
        ]
        setSubmitting(true)
        setSubmitError(null)
        try {
            if (instructionsLabel) await onSelect(targets, instructions)
            else await onSelect(targets)
            modal.remove()
        } catch (error) {
            setSubmitError(error instanceof Error ? error.message : "Could not start the selected agent")
        } finally {
            setSubmitting(false)
        }
    }
    const close = () => {
        if (!submitting) modal.remove()
    }

    return (
        <Modal
            title={actionName}
            closeDisabled={submitting}
            onClose={close}
            footer={
                <div className="flex w-full flex-wrap items-center justify-between gap-3">
                    <span className="text-xs tabular-nums text-muted">
                        {selectedOptions.length} of {targetRange.maxTargets} selected
                    </span>
                    <div className="flex items-center gap-2 max-[600px]:w-full">
                        <button
                            type="button"
                            className="btn h-9 px-3 text-sm text-muted hover:bg-base-200 hover:text-base-content disabled:opacity-45"
                            disabled={submitting}
                            onClick={close}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn h-9 bg-primary px-4 text-sm font-semibold text-primary-content hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-45 max-[600px]:flex-1"
                            disabled={loading || submitting || selectedOptions.length < targetRange.minTargets}
                            onClick={() => void submit()}
                        >
                            {submitting
                                ? (pendingLabel ?? "Starting…")
                                : (submitLabel ??
                                  (allowsMultiple
                                      ? `Run ${actionName} with ${selectedOptions.length} model${selectedOptions.length === 1 ? "" : "s"}`
                                      : `Run ${actionName}`))}
                        </button>
                    </div>
                </div>
            }
        >
            <div className="flex flex-col gap-6">
                {submitError && (
                    <div role="alert" className="border border-error/40 bg-error/10 p-3 text-sm text-error">
                        {submitError}
                    </div>
                )}
                {instructionsLabel && (
                    <label className="flex flex-col gap-2 text-sm text-muted">
                        {instructionsLabel}
                        <textarea
                            value={instructions}
                            onChange={(event) => setInstructions(event.target.value)}
                            maxLength={8000}
                            disabled={submitting}
                            rows={2}
                            className="border border-border bg-base-200 p-3 text-base-content outline-none focus:border-primary"
                            placeholder="Pick up where it stopped, or describe a change of approach"
                        />
                    </label>
                )}

                {(description || currentAgent) && (
                    <div className="flex flex-wrap items-start justify-between gap-3 max-[600px]:flex-col">
                        {description && <p className="min-w-0 flex-1 text-sm leading-5 text-muted">{description}</p>}
                        {currentAgent && currentTarget && (
                            <div
                                className="ml-auto inline-flex shrink-0 items-center gap-1.5 border border-border bg-base-200 px-2 py-1 text-[11px] text-muted max-[600px]:ml-0"
                                aria-label={`Current agent: ${currentAgent.modelLabel} on ${currentAgent.harnessLabel}`}
                            >
                                <span>Current</span>
                                <AgentProviderMark harnessId={currentTarget.harnessId} className="h-3 w-3" />
                                <span className="font-semibold text-base-content">
                                    {currentAgent.modelLabel} · {currentTarget.thinking ?? "Max"}
                                </span>
                            </div>
                        )}
                    </div>
                )}

                <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
                    <span>Thinking for selection</span>
                    <ThinkingPicker value={thinking} onChange={changeThinking} disabled={submitting} ariaLabel="Agent thinking level" side="bottom" />
                </div>
                {selectedOptions.length > 0 && (
                    <div className="flex flex-wrap gap-2" aria-label="Selected agents">
                        {selectedOptions.map((option) => (
                            <button
                                type="button"
                                key={option.id}
                                className="btn border border-primary/30 bg-primary/5 px-2 py-1 text-xs"
                                disabled={submitting}
                                aria-label={`Remove ${option.modelLabel} ${option.thinking ?? "max"}`}
                                onClick={() => setSelectedTargets((current) => current.filter((target) => sessionActionTargetId(target) !== option.id))}
                            >
                                {option.modelLabel} · {option.thinking ?? "Max"} ×
                            </button>
                        ))}
                    </div>
                )}
                {allowsMultiple && (
                    <div className="border border-primary/25 bg-primary/5 px-3 py-2 text-sm leading-5 text-base-content">
                        Choose up to {targetRange.maxTargets} models and thinking levels. The Action defines each selection's agent roles.
                    </div>
                )}

                {!loading && !error && options.length > 0 && (
                    <label className="flex h-10 items-center gap-2 border border-border bg-base-200 px-3 text-muted focus-within:border-primary">
                        <Search size={14} aria-hidden="true" />
                        <input
                            type="search"
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            placeholder={`Search ${options.length} models`}
                            aria-label="Search action models"
                            className="min-w-0 flex-1 appearance-none border-0 bg-transparent p-0 text-sm text-base-content outline-none placeholder:text-muted"
                        />
                        <span className="text-[11px] tabular-nums text-muted">{visibleOptionCount}</span>
                    </label>
                )}

                {loading && (
                    <div className="flex min-h-28 items-center justify-center gap-2 text-sm text-muted">
                        <LoaderCircle size={16} className="animate-spin" />
                        Loading available agents…
                    </div>
                )}

                {!loading && error && (
                    <div className="border border-error/40 bg-error/10 p-3">
                        <div className="text-sm text-error">{error}</div>
                        <button
                            type="button"
                            className="btn mt-3 inline-flex h-8 items-center gap-2 border border-border bg-base-100 px-3 text-xs text-base-content hover:bg-base-200"
                            onClick={() => void load()}
                        >
                            <RotateCcw size={12} />
                            Retry
                        </button>
                    </div>
                )}

                {!loading && !error && options.length > 0 && (
                    <>
                        <OptionSection
                            label="Pinned"
                            options={visiblePinned}
                            currentTarget={currentTarget}
                            selectedOptionIds={selectedOptionIds}
                            selectionAtLimit={selectionAtLimit}
                            onSelect={select}
                        />
                        <OptionSection
                            label="More models"
                            options={other}
                            currentTarget={currentTarget}
                            selectedOptionIds={selectedOptionIds}
                            selectionAtLimit={selectionAtLimit}
                            onSelect={select}
                        />
                        <OptionSection
                            label="Legacy"
                            options={legacy}
                            currentTarget={currentTarget}
                            selectedOptionIds={selectedOptionIds}
                            selectionAtLimit={selectionAtLimit}
                            onSelect={select}
                        />
                        <OptionSection
                            label="Provider defaults"
                            options={defaults}
                            currentTarget={currentTarget}
                            selectedOptionIds={selectedOptionIds}
                            selectionAtLimit={selectionAtLimit}
                            onSelect={select}
                        />
                        {visibleOptionCount === 0 && <div className="py-8 text-center text-sm text-muted">No models match “{query}”.</div>}
                    </>
                )}

                {!loading && !error && options.length === 0 && (
                    <div className="border border-warning/40 bg-warning/10 p-3 text-sm text-base-content">
                        No available agent models were reported for this task.
                        {unavailableHarnesses.length > 0 && (
                            <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-muted">
                                {unavailableHarnesses.map((harness) => (
                                    <li key={harness.harnessId}>
                                        {harness.label}: {harness.status || (harness.installed ? "Unavailable" : "Not installed")}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </div>
        </Modal>
    )
})
